/**
 * charNormalizer.ts - Normalización de caracteres equivalentes
 * 
 * Pre-procesa texto para unificar variantes de caracteres antes
 * de aplicar patrones de corrección mojibake.
 * 
 * PROBLEMA RESUELTO:
 * Un mismo patrón mojibake puede aparecer con variantes:
 * - `Ì_` vs `Ì ` vs `Ì` (underscore, espacio, nada)
 * - `'` vs `'` vs `ʼ` (comillas diferentes)
 * - ` ` vs `\u00A0` (espacio vs NBSP)
 * 
 * En lugar de crear 43+ patrones adicionales, normalizamos
 * estos caracteres a una forma canónica antes de aplicar patrones.
 * 
 * @version 1.0.0
 * @date 2025-12-23
 */

// ============================================================================
// MAPAS DE NORMALIZACIÓN
// ============================================================================

/**
 * Espacios → espacio normal (U+0020)
 * Crítico para parsing de coordenadas y direcciones
 */
const SPACE_VARIANTS: Map<string, string> = new Map([
  ['\u00A0', ' '],  // NBSP - muy común en Word/PDF
  ['\u2002', ' '],  // En space
  ['\u2003', ' '],  // Em space
  ['\u2009', ' '],  // Thin space
  ['\u200A', ' '],  // Hair space
  ['\u202F', ' '],  // Narrow NBSP
  ['\u205F', ' '],  // Medium mathematical space
  ['\u3000', ' '],  // Ideographic space
]);

/**
 * Comillas simples → apóstrofo recto (U+0027)
 * Necesario para patrones de triple encoding que contienen comillas
 * 
 * NOTA: NO incluir ´ (U+00B4 acute accent) porque es parte
 * de patrones mojibake legítimos como Â´ → ´
 */
const QUOTE_VARIANTS: Map<string, string> = new Map([
  ['\u2019', '\x27'],  // Right single quotation mark → '
  ['\u2018', '\x27'],  // Left single quotation mark → '
  ['\u02BC', '\x27'],  // Modifier letter apostrophe → '
  ['\u02B9', '\x27'],  // Modifier letter prime → '
  ['\u0060', '\x27'],  // Grave accent → '
  // NO incluir \u00B4 (acute accent) - es parte de patrones mojibake
]);

/**
 * Comillas dobles → comilla recta (U+0022)
 */
const DOUBLE_QUOTE_VARIANTS: Map<string, string> = new Map([
  ['\u201C', '\x22'],  // Left double quotation mark → "
  ['\u201D', '\x22'],  // Right double quotation mark → "
  ['\u201E', '\x22'],  // Double low-9 quotation mark → "
  ['\u00AB', '\x22'],  // Left guillemet → "
  ['\u00BB', '\x22'],  // Right guillemet → "
]);

/**
 * Guiones bajos → underscore normal (U+005F)
 */
const UNDERSCORE_VARIANTS: Map<string, string> = new Map([
  ['\uFF3F', '_'],  // Fullwidth low line → _
  ['\u2017', '_'],  // Double low line → _
]);

/**
 * Guiones → guion normal (U+002D)
 * Importante para rangos de coordenadas
 */
const HYPHEN_VARIANTS: Map<string, string> = new Map([
  ['\u2010', '-'],  // Hyphen → -
  ['\u2011', '-'],  // Non-breaking hyphen → -
  ['\u2012', '-'],  // Figure dash → -
  ['\u2013', '-'],  // En dash → -
  ['\u2014', '-'],  // Em dash → -
  ['\u2015', '-'],  // Horizontal bar → -
  ['\uFE63', '-'],  // Small hyphen-minus → -
  ['\uFF0D', '-'],  // Fullwidth hyphen-minus → -
]);

// ============================================================================
// REGEX COMPILADOS PARA RENDIMIENTO
// ============================================================================

const SPACE_REGEX = new RegExp(`[${[...SPACE_VARIANTS.keys()].join('')}]`, 'g');
const QUOTE_REGEX = new RegExp(`[${[...QUOTE_VARIANTS.keys()].join('')}]`, 'g');
const DOUBLE_QUOTE_REGEX = new RegExp(`[${[...DOUBLE_QUOTE_VARIANTS.keys()].join('')}]`, 'g');
const UNDERSCORE_REGEX = new RegExp(`[${[...UNDERSCORE_VARIANTS.keys()].join('')}]`, 'g');
const HYPHEN_REGEX = new RegExp(`[${[...HYPHEN_VARIANTS.keys()].join('')}]`, 'g');

// ============================================================================
// FUNCIONES DE NORMALIZACIÓN
// ============================================================================

/**
 * Normaliza espacios variantes a espacio normal
 */
export function normalizeSpaces(text: string): string {
  return text.replace(SPACE_REGEX, ' ');
}

/**
 * Normaliza comillas simples variantes a apóstrofo recto
 */
export function normalizeQuotes(text: string): string {
  return text.replace(QUOTE_REGEX, '\x27');
}

/**
 * Normaliza comillas dobles variantes a comilla recta
 */
export function normalizeDoubleQuotes(text: string): string {
  return text.replace(DOUBLE_QUOTE_REGEX, '\x22');
}

/**
 * Normaliza underscores variantes
 */
export function normalizeUnderscores(text: string): string {
  return text.replace(UNDERSCORE_REGEX, '_');
}

/**
 * Normaliza guiones variantes a guion normal
 */
export function normalizeHyphens(text: string): string {
  return text.replace(HYPHEN_REGEX, '-');
}

/**
 * Aplica todas las normalizaciones en orden óptimo.
 * 
 * ORDEN IMPORTANTE:
 * 1. Espacios primero (más frecuentes, no afectan otros)
 * 2. Comillas (necesarias para patrones de encoding)
 * 3. Underscores (para patrones NFD)
 * 4. Guiones (para coordenadas)
 * 
 * @param text - Texto a normalizar
 * @returns Texto con caracteres normalizados
 */
export function normalizeVariantChars(text: string): string {
  if (!text || text.length === 0) return text;
  
  let result = text;
  
  // Aplicar en orden de frecuencia/impacto
  result = normalizeSpaces(result);
  result = normalizeQuotes(result);
  result = normalizeDoubleQuotes(result);
  result = normalizeUnderscores(result);
  result = normalizeHyphens(result);
  
  return result;
}

/**
 * Verifica si el texto contiene caracteres variantes que serían normalizados
 */
export function hasVariantChars(text: string): boolean {
  if (!text) return false;
  
  return (
    SPACE_REGEX.test(text) ||
    QUOTE_REGEX.test(text) ||
    DOUBLE_QUOTE_REGEX.test(text) ||
    UNDERSCORE_REGEX.test(text) ||
    HYPHEN_REGEX.test(text)
  );
}

/**
 * Estadísticas de normalización para debugging
 */
export interface NormalizationStats {
  spacesNormalized: number;
  quotesNormalized: number;
  doubleQuotesNormalized: number;
  underscoresNormalized: number;
  hyphensNormalized: number;
  totalNormalized: number;
}

/**
 * Normaliza con estadísticas detalladas
 */
export function normalizeWithStats(text: string): {
  result: string;
  stats: NormalizationStats;
} {
  if (!text || text.length === 0) {
    return {
      result: text,
      stats: {
        spacesNormalized: 0,
        quotesNormalized: 0,
        doubleQuotesNormalized: 0,
        underscoresNormalized: 0,
        hyphensNormalized: 0,
        totalNormalized: 0,
      },
    };
  }
  
  const stats: NormalizationStats = {
    spacesNormalized: 0,
    quotesNormalized: 0,
    doubleQuotesNormalized: 0,
    underscoresNormalized: 0,
    hyphensNormalized: 0,
    totalNormalized: 0,
  };
  
  let result = text;
  
  // Contar y normalizar espacios
  const spaceMatches = result.match(SPACE_REGEX);
  stats.spacesNormalized = spaceMatches?.length ?? 0;
  result = normalizeSpaces(result);
  
  // Contar y normalizar comillas simples
  const quoteMatches = result.match(QUOTE_REGEX);
  stats.quotesNormalized = quoteMatches?.length ?? 0;
  result = normalizeQuotes(result);
  
  // Contar y normalizar comillas dobles
  const dblQuoteMatches = result.match(DOUBLE_QUOTE_REGEX);
  stats.doubleQuotesNormalized = dblQuoteMatches?.length ?? 0;
  result = normalizeDoubleQuotes(result);
  
  // Contar y normalizar underscores
  const underscoreMatches = result.match(UNDERSCORE_REGEX);
  stats.underscoresNormalized = underscoreMatches?.length ?? 0;
  result = normalizeUnderscores(result);
  
  // Contar y normalizar guiones
  const hyphenMatches = result.match(HYPHEN_REGEX);
  stats.hyphensNormalized = hyphenMatches?.length ?? 0;
  result = normalizeHyphens(result);
  
  stats.totalNormalized =
    stats.spacesNormalized +
    stats.quotesNormalized +
    stats.doubleQuotesNormalized +
    stats.underscoresNormalized +
    stats.hyphensNormalized;
  
  return { result, stats };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  normalizeVariantChars,
  normalizeSpaces,
  normalizeQuotes,
  normalizeDoubleQuotes,
  normalizeUnderscores,
  normalizeHyphens,
  hasVariantChars,
  normalizeWithStats,
};
